import { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SaasProductDto } from '@cms/models';
import { saasApi, type TenantSubscriptionWithRelations } from '@/lib/api/saas.api';
import { useAuthStore } from '@/stores/authStore';
import { withAreaPrefix } from '@/lib/area-routes';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Tenant-area products & plan summary (`/tenant/my-product`). */
export function TenantAreaMyProductPage() {
  const location = useLocation();
  const tenantId = useAuthStore(s => s.tenantId);
  const hasScope = useAuthStore(s => s.hasScope);
  const canProducts = hasScope('product:read');
  const canPlans = hasScope('plan:read');

  if (!canProducts && !canPlans) {
    return <Navigate to={withAreaPrefix('/unauthorized', location.pathname)} replace />;
  }

  const tid = tenantId?.trim() ?? '';

  const productsQuery = useQuery({
    queryKey: ['tenant-area-my-product', 'products', tid],
    queryFn: () => saasApi.getSaasProducts(tid || undefined),
    enabled: Boolean(tid) && canProducts,
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['tenant-area-my-product', 'tenant-subscriptions', tid],
    queryFn: () => saasApi.getTenantSubscriptions(tid || undefined),
    enabled: Boolean(tid) && canPlans,
  });

  const currentQuery = useQuery({
    queryKey: ['tenant-area-my-product', 'current-plan', tid],
    queryFn: () => saasApi.getCurrentSubscription(tid),
    enabled: Boolean(tid) && canPlans,
  });

  const products = (productsQuery.data ?? []) as SaasProductDto[];
  const subs: TenantSubscriptionWithRelations[] = subscriptionsQuery.data ?? [];

  const currentPlanLabel = useMemo(() => {
    const cur = currentQuery.data as TenantSubscriptionWithRelations | null | undefined;
    if (!cur) return null;
    const plan = cur.plan;
    return plan?.name?.trim() || plan?.code?.trim() || null;
  }, [currentQuery.data]);

  if (!tid) {
    return (
      <div className="space-y-2 p-4">
        <h1 className="text-2xl font-bold">My product</h1>
        <p className="text-sm text-muted-foreground">No tenant is associated with this account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight md:text-3xl">My product</h1>
        <p className="text-sm text-muted-foreground">
          Products and plan entitlements for your organization.
        </p>
      </header>

      {canPlans ? (
        <section className="rounded-xl border bg-card p-5">
          <h2 className="text-base font-semibold">Plan &amp; subscription</h2>
          {currentQuery.isLoading ? (
            <p className="mt-2 text-sm text-muted-foreground">Loading current plan…</p>
          ) : currentPlanLabel ? (
            <p className="mt-2 text-sm">
              Active plan: <span className="font-medium text-foreground">{currentPlanLabel}</span>
            </p>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">No active plan subscription on file.</p>
          )}
          {subscriptionsQuery.isLoading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading subscription history…</p>
          ) : subs.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No tenant subscription records.</p>
          ) : (
            <ul className="mt-4 space-y-2 text-sm">
              {subs.slice(0, 10).map(row => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg border border-border/60 px-3 py-2"
                >
                  <span className="text-foreground">
                    {row.plan?.name ?? row.plan?.code ?? 'Plan'}{' '}
                    <span className="text-muted-foreground">
                      {row.isActive ? '(active)' : '(inactive)'}
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(row.startsAt)} → {formatDate(row.endsAt ?? undefined)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {canProducts ? (
        <section className="rounded-xl border bg-card p-5">
          <h2 className="text-base font-semibold">Products</h2>
          {productsQuery.isLoading ? (
            <p className="mt-2 text-sm text-muted-foreground">Loading products…</p>
          ) : products.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No products visible for your tenant.</p>
          ) : (
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {products.map(p => (
                <li key={p.id} className="rounded-lg border border-border/60 p-4">
                  <p className="font-medium text-foreground">{p.name ?? p.code}</p>
                  {p.description ? (
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{p.description}</p>
                  ) : null}
                  <p className="mt-2 text-xs font-mono text-muted-foreground">{p.code}</p>
                  <p className="mt-1 text-xs capitalize text-muted-foreground">Status: {p.status}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
